import type { ITransaction } from "@/modules/transaction/common/types";
import { useGetTransactionsQuery } from "@/modules/transaction/services";
import { DATE_FORMAT } from "@/shared/common/constants";
import { AppPaths } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { EmptyOrErrorContent, SeeMore } from "@/shared/components";
import { HistoryItem } from "@/shared/components/HistoryItem";
import {
  buildRoute,
  formatDate,
  getErrorMessage,
  getFormattedHistoryList,
} from "@/shared/helpers";
import { useAppDispatch } from "@/shared/hooks";
import { setActiveTransactionId, setActiveTransactionType } from "@/shared/state/common";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const TransactionHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    data: list,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetTransactionsQuery({}, {
    refetchOnMountOrArgChange: true,
  });

  const historyList = useMemo(() => {
    return getFormattedHistoryList({ list: list ?? [] });
  }, [list]);

  const handleSeeMore = () => {
    const url = buildRoute(AppPaths.TransactionHistory, {});
    navigate(url);
  };

  const onShowDetails = ({ id, transactionType }: ITransaction) => {
    dispatch(setActiveTransactionId(id));
    dispatch(setActiveTransactionType(transactionType));
    navigate(AppPaths.TransactionHistory);
  };

  return (
    <div className="full-width card widget-content history-container">
      <div className="flexbox align-items--center">
        <Heading className="content-title" weight="bold" size="xsmall">
          {t("dashboard.transactionHistory.title")}
        </Heading>
        <SeeMore className="ml-8" action={handleSeeMore}/>
      </div>
      <div className="full-width widget mt-20">
        {!historyList?.length && !isError && !isFetching ? (
          <div className="full-width flexbox align-items--center pt-64 pb-64 mt-6 mb-6">
            <EmptyOrErrorContent isShowButton={false}/>
          </div>
        ) : isError && !isFetching ? (
          <div className="full-width flexbox align-items--center pt-64 pb-64 mt-6 mb-6">
            <EmptyOrErrorContent
              isError={true}
              title={t(getErrorMessage(error as IError))}
              action={refetch}
            />
          </div>
        ) : (
          isFetching ? (
            <div
              className="full-width flexbox justify-content--center align-items--center loader-wrapper">
              <Spinner isFullScreen={false} size="small"/>
            </div>
          ) : (
            historyList?.map((history, i) => (
              <Fragment key={history?.creationDate}>
                <Text className="mt-24 mb-6 pl-24 history-date" type="secondary" size="medium">
                  {formatDate(history.creationDate, DATE_FORMAT.DD_MMM_YYYY)}
                </Text>
                {history.transactions.map((transaction, index) => (
                  <div
                    onClick={() => onShowDetails(transaction)}
                    key={transaction.id}
                  >
                    <HistoryItem
                      isInWidget
                      {...transaction}
                      isLast={
                        i === historyList.length - 1 && index === history.transactions.length - 1
                      }
                    />
                  </div>
                ))}
              </Fragment>
            ))
          ))}
      </div>
    </div>
  );
};
